<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('attendanceDevice.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('attendanceDevice.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <ts-button
            outline
            color="info"
            @click.prevent="() => (show_form = true)"
            >{{ $t('add_new') }}</ts-button
          >
          <div class="d-flex">
            <ts-input
              v-model="search"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
            <ts-button
              outline
              class="ml-2"
              v-tooltip="$t('filter')"
              @click.prevent="visible = true"
            >
              <i class="fa fa-filter"></i>
            </ts-button>
          </div>
        </div>
        <!-- drawer -->
        <a-drawer
          :title="$t('invoice.filter')"
          placement="right"
          :closable="false"
          :visible="visible"
          @close="() => (visible = false)"
          :zIndex="1020"
          width="300px"
        >
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <ts-branch-filter @filter="value => (branch_id = value)" />
              </div>
            </div>
          </div>
        </a-drawer>
      </ts-card-wrapper>
      <!-- table list -->
      <div class="overflow-auto">
        <ts-table
          :columns="columns"
          :records="resources"
          :loading="loading"
          :rowGroup="{
            field: 'branch.branch_name_en'
          }"
        >
          <template v-slot="{ record }">
            <td>{{ record.device_ip }}</td>
            <td>{{ record.device_port }}</td>
            <td>{{ record.att_device_type.device_type }}</td>
            <td>{{ record.device_description }}</td>
            <td class="ts-whitespace-no-wrap text-center">
              <a href="#" class="text-primary" @click.prevent="edit(record)">
                <i class="fa fa-edit"></i>
              </a>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
    </ts-card>
    <!-- form action -->
    <a-modal
      v-model="show_form"
      :title="$t('attendanceDevice.pageTitle')"
      :zIndex="1020"
      :footer="null"
      :maskClosable="false"
      @cancel="clearInput"
      width="20%"
    >
      <form-component v-model="model" :validate="errors">
        <template v-slot:action-button>
          <ts-button class="mr-2" @click.prevent="cancelCreate">{{
            $t('cancel')
          }}</ts-button>
          <ts-button
            v-if="model.id == 0"
            class="mr-2"
            color="primary"
            outline
            :waiting="waiting_new"
            @click.prevent="saveNew"
            >{{ $t('save_add_new') }}</ts-button
          >
          <ts-button
            v-if="model.id == 0"
            color="primary"
            :waiting="waiting"
            @click.prevent="save"
            >{{ $t('save') }}</ts-button
          >
          <ts-button
            v-if="model.id != 0"
            color="primary"
            :waiting="waiting"
            @click.prevent="onUpdate"
            >{{ $t('update') }}</ts-button
          >
        </template>
      </form-component>
    </a-modal>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapState, mapActions } from 'vuex'
import FormComponent from './form'
import { debounce } from 'lodash'

export default {
  name: 'attendanceDevice',
  components: {
    FormComponent
  },
  data() {
    return {
      visible: false,
      loading: false,
      show_form: false,
      waiting: false,
      waiting_new: false,
      branch_id: [],
      errors: new Errors(),
      model: {
        id: 0,
        device_ip: null,
        device_description: null,
        device_type_id: null,
        device_active: true
      }
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('attendanceDevice', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.attendanceDevice.search
      },
      set(newValue) {
        this.$store.commit('attendanceDevice/SET_SEARCH', newValue)
        this.$store.commit('attendanceDevice/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        { name: this.$t('attendanceDevice.deviceIpId') },
        { name: this.$t('attendanceDevice.port') },
        { name: this.$t('attendanceDevice.type') },
        {
          name: this.$t('attendanceDevice.description')
        },
        { name: this.$t('actions'), style: 'text-align:center; width:7%' }
      ]
    }
  },
  methods: {
    ...mapActions('attendanceDevice', ['store', 'update']),
    save() {
      this.waiting = true
      this.store(this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.show_form = false
          this.clearInput()
          this.fetchData()
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    saveNew() {
      this.waiting_new = true
      this.store(this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.clearInput()
          this.fetchData()
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting_new = false
        })
    },
    onUpdate() {
      this.waiting = true
      this.update({
        id: this.model.id,
        data: this.model
      })
        .then(response => {
          this.$toasted.success(response.message)
          this.show_form = false
          this.clearInput()
          this.fetchData()
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch(
          'attendanceDevice/fetch',
          Object.assign(
            {
              branch_id: this.branch_id
            },
            { ...attributes }
          )
        )
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    edit(record) {
      this.model.id = record.id
      this.model.device_ip = record.device_ip
      this.model.device_description = record.device_description
      this.model.device_type_id = record.device_type_id
      this.model.device_active = record.device_active
      this.show_form = true
    },
    cancelCreate() {
      this.show_form = false
      this.clearInput()
    },
    clearInput() {
      this.errors = new Errors()
      this.model.id = 0
      this.model.device_ip = null
      this.model.device_description = null
      this.model.device_type_id = null
      this.model.device_active = null
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData({ search, branch_id: this.branch_id })
    }, 500),
    branch_id: debounce(function(value) {
      this.fetchData({ branch_id: value })
    }, 500)
  }
}
</script>
